import { VisibleStepMetadata, TableRowMetadata } from "pages/application/applicationService";
import "./Metadata.scss";
import useDesktopChecker from "hooks/useDesktopChecker";
import { GLOBAL_LINK_NO_TX_CLASS } from "utilities/globalLinkNoTx";
import {
  isCurrencyCell,
  isDateRangeCell,
  isIncomeListSection,
  shouldProtectIncomeName,
  splitDateCellForTranslation,
} from "./metadataIncomeTableTranslation";
import { isTranslatableAssetSource } from "pages/application/templates/Summary/Finances/financesSummaryTranslation";

/** Section labels API sends in `text` with empty `header` — allow GlobalLink translate. */
const TRANSLATABLE_SECTION_LABELS = new Set(["Owned Property"]);

const isLabelOnlyEntry = (item: VisibleStepMetadata): boolean =>
  !item.header?.trim() &&
  Boolean(item.text) &&
  TRANSLATABLE_SECTION_LABELS.has(item.text.trim()) &&
  !item.table &&
  !item.childItems?.length;

const cellNoTxClass = (prevent: boolean, extraClass?: string): string | undefined => {
  const parts = [extraClass, prevent ? GLOBAL_LINK_NO_TX_CLASS : ""].filter(Boolean);
  return parts.length ? parts.join(" ") : undefined;
};

const renderIncomeTableCell = (value: string, columnIndex: number, columns: string[]) => {
  if (columnIndex === 0) {
    return (
      <span
        className={cellNoTxClass(shouldProtectIncomeName(columns))}
        data-testid="metadata-table-cell"
      >
        {value}
      </span>
    );
  }

  if (columnIndex === 1 || isCurrencyCell(value)) {
    return (
      <span
        className={cellNoTxClass(true, columnIndex === 1 ? "data__table__row__middleColumn" : undefined)}
        data-testid="metadata-table-cell"
      >
        {" "}
        {value}
      </span>
    );
  }

  if (isDateRangeCell(value)) {
    const parts = splitDateCellForTranslation(value);
    return (
      <span data-testid="metadata-table-cell">
        {" "}
        {parts.map((part, index) => (
          <span
            key={`${part.kind}-${index}`}
            className={part.kind === "protected" ? GLOBAL_LINK_NO_TX_CLASS : ""}
          >
            {part.text}
          </span>
        ))}
      </span>
    );
  }

  // Empty date / unknown — keep protected
  return (
    <span className={GLOBAL_LINK_NO_TX_CLASS} data-testid="metadata-table-cell">
      {" "}
      {value}
    </span>
  );
};

const Metadata = ({ metadata }: { metadata: VisibleStepMetadata[] }) => {
  const isDesktop = useDesktopChecker();
  const hasNoTable = Array.isArray(metadata) && metadata[0]?.table == null;
  return (
    <div className={`data ${hasNoTable ? "no-table": ""}`}>
      {metadata.map((item) => (
        <div key={`${item.header}-${item.text}`}>
          {isLabelOnlyEntry(item) ? (
            <div className={`data__label ${hasNoTable ? "no-table" : ""}`}>{item.text}</div>
          ) : (
            <>
              {item.header && (
                <div className={`data__label ${hasNoTable ? "no-table" : ""}`}>{item.header}</div>
              )}
              {item.text && (
                <div className={`data__values ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="metadata-value">
                  {item.text}
                </div>
              )}
            </>
          )}
          {item.table && (
            <div className="data__table">
              {item.table.rows.map((row: TableRowMetadata) => {
                const useIncomeRules = isIncomeListSection(item.header);
                return (
                  <div key={row.columns[0]} className="data__table__row">
                    {useIncomeRules ? (
                      <>
                        {renderIncomeTableCell(row.columns[0] ?? "", 0, row.columns)}
                        {renderIncomeTableCell(row.columns[1] ?? "", 1, row.columns)}
                        {(isDesktop || row.columns[2]) &&
                          renderIncomeTableCell(row.columns[2] ?? "", 2, row.columns)}
                      </>
                    ) : (
                      <>
                        <span className={cellNoTxClass(!isTranslatableAssetSource(row.columns[0]))} data-testid="metadata-table-cell">{row.columns[0]}</span>
                        <span className={`data__table__row__middleColumn ${GLOBAL_LINK_NO_TX_CLASS}`} data-testid="metadata-table-cell"> {row.columns[1]}</span>
                        {(isDesktop || row.columns[2]) &&  <span className={GLOBAL_LINK_NO_TX_CLASS} data-testid="metadata-table-cell"> {row.columns[2]}</span>}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {item.childItems &&
            <ul>
              {item.childItems?.map(({ text }: VisibleStepMetadata) => (
                <li key={text} className={GLOBAL_LINK_NO_TX_CLASS} data-testid="metadata-list-item">{text}</li>
              ))}
            </ul>
          }
        </div>
      ))}
    </div>
  );
};

export default Metadata;
